/*eslint max-lines-per-function: ["error", 510]*/
import React, { useCallback } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import type {
  GridGetterFunction,
  GridCountFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { SalesInvoiceSearch } from '../../components/SalesInvoice/SalesInvoicesSearch';
import {
  KSEFStatusEnum,
  SalesInvoiceStatusEnum,
} from '../../../common/spdCore/validation/enums';
import { JobConfigInfo } from '../../components/JobConfig/JobConfigInfo';
import { JobTypeEnum } from '../../../common/spdCore/validation/schemas';
import { GenerateAutoInvoiceModal } from '../../components/SalesInvoice/GenerateAutoInvoiceModal';

enum SalesInvoicesTabs {
  DRAFTS = 'drafts',
  AUTO_DRAFTS = 'auto-drafts',
  KSEF_WAITING = 'ksef-waiting',
  KSEF_REJECTED = 'ksef-rejected',
  UNPAID = 'unpaid',
  OVERPAID = 'overpaid',
  AUTO = 'auto',
  ALL = 'all',
}

export const SalesInvoicesView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();

  const getDataDrafts: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Draft,
      ),
    [spd.salesInvoices],
  );

  const getDataAutoDrafts: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Draft,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      ),
    [spd.salesInvoices],
  );

  const getDataAuto: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      ),
    [spd.salesInvoices],
  );

  const getDataAll: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.salesInvoices],
  );

  const getDataKsefWaiting: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        KSEFStatusEnum.WaitingForKSeF,
      ),
    [spd.salesInvoices],
  );

  const getDataKsefRejected: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        KSEFStatusEnum.RejectedInKSeF,
      ),
    [spd.salesInvoices],
  );

  const getCountDrafts: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Draft,
      ),
    [spd.salesInvoices],
  );

  const getCountAutoDrafts: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Draft,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      ),
    [spd.salesInvoices],
  );

  const getCountAuto: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      ),
    [spd.salesInvoices],
  );

  const getCountAll: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(filterText, filterColumns),
    [spd.salesInvoices],
  );

  const getCountKsefWaiting: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        KSEFStatusEnum.WaitingForKSeF,
      ),
    [spd.salesInvoices],
  );

  const getCountKsefRejected: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        KSEFStatusEnum.RejectedInKSeF,
      ),
    [spd.salesInvoices],
  );

  const getDataUnpaid: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        `${SalesInvoiceStatusEnum.Approved},${SalesInvoiceStatusEnum.InKSeF}`,
      ),
    [spd.salesInvoices],
  );

  const getCountUnpaid: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
        `${SalesInvoiceStatusEnum.Approved},${SalesInvoiceStatusEnum.InKSeF}`,
      ),
    [spd.salesInvoices],
  );

  const getDataOverpaid: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.salesInvoices.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      ),
    [spd.salesInvoices],
  );

  const getCountOverpaid: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      ),
    [spd.salesInvoices],
  );

  const handleAdd = () => {
    nav.salesInvoiceEditor();
  };

  const handleRowClick = (id: string) => {
    nav.salesInvoiceEditor(id);
  };

  const [activeTab, setActiveTab] = React.useState<string>(
    SalesInvoicesTabs.DRAFTS,
  );
  const [showAutoModal, setShowAutoModal] = React.useState(false);
  const [autoDraftsKey, setAutoDraftsKey] = React.useState(0);

  const handleAutoModalSuccess = () => {
    setAutoDraftsKey(k => k + 1);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">Faktury sprzedaży</Card.Title>
        <TabsHistory
          activeKey={activeTab}
          setActiveTab={setActiveTab}
          id="sales-invoices"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={SalesInvoicesTabs.DRAFTS}
            title={'Szkice'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataDrafts}
              getCount={getCountDrafts}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.AUTO_DRAFTS}
            title={'Szkice automatyczne'}
            className="m-3"
          >
            <SalesInvoiceSearch
              key={autoDraftsKey}
              getData={getDataAutoDrafts}
              getCount={getCountAutoDrafts}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
            />
            <JobConfigInfo
              items={[
                {
                  jobType: JobTypeEnum.AutoInvoiceGeneration,
                  nextStartLabel:
                    'Następne uruchomienie automatycznego fakturowania',
                  frequencyLabel: 'Częstotliwość automatycznego fakturowania',
                },
              ]}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.KSEF_WAITING}
            title={'KSeF - oczekujące'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataKsefWaiting}
              getCount={getCountKsefWaiting}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
            />
            <JobConfigInfo
              items={[
                {
                  jobType: JobTypeEnum.KsefUpoPoll,
                  nextStartLabel: 'Następne uruchomienie eksportu do KSeF',
                  frequencyLabel: 'Częstotliwość eksportu do KSeF',
                },
              ]}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.KSEF_REJECTED}
            title={'KSeF - odrzucone'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataKsefRejected}
              getCount={getCountKsefRejected}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.UNPAID}
            title={'Nieopłacone'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataUnpaid}
              getCount={getCountUnpaid}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
              showExport={true}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.OVERPAID}
            title={'Do odwiązania'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataOverpaid}
              getCount={getCountOverpaid}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
              showExport={true}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.AUTO}
            title={'Automatyczne'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataAuto}
              getCount={getCountAuto}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
            />
            <JobConfigInfo
              items={[
                {
                  jobType: JobTypeEnum.AutoInvoiceGeneration,
                  nextStartLabel:
                    'Następne uruchomienie automatycznego fakturowania',
                  frequencyLabel: 'Częstotliwość automatycznego fakturowania',
                },
              ]}
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.ALL}
            title={'Wszystkie'}
            className="m-3"
          >
            <SalesInvoiceSearch
              getData={getDataAll}
              getCount={getCountAll}
              onRowClick={handleRowClick}
              withFilterInColumns={true}
              showFilter={true}
            />
          </Tab>
        </TabsHistory>
        <div className="d-flex gap-2 mb-4">
          <Button variant="outline-primary" onClick={handleAdd}>
            {'Nowa faktura'}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => setShowAutoModal(true)}
          >
            {'Nowa wyliczona faktura'}
          </Button>
        </div>
        <GenerateAutoInvoiceModal
          show={showAutoModal}
          onHide={() => setShowAutoModal(false)}
          onSuccess={handleAutoModalSuccess}
        />
      </Card.Body>
    </Card>
  );
};
